import { EQ_BANDS, normaliseGains } from '../../domain/eq';
import { type BackoffPolicy, backoffDelay, STREAM_BACKOFF, shouldRetry } from '../../domain/retry';
import type { Station } from '../../domain/stations';
import type {
  AnalyserLike,
  AudioCapabilities,
  AudioErrorKind,
  AudioPort,
  PlaybackState,
  PlayOptions,
} from '../../ports/audio';
import { createAudioEmitter } from './emitter';
import { attachHls, type HlsSession, hlsUsesMse, loadedHls, loadHls } from './hls-attach';

/**
 * Predvajalnik nad `<audio>`, z Web Audio grafom **samo takrat, ko je potreben**.
 *
 * Vse, kar bi lahko poklicalo `new Audio()` ali `AudioContext`, je v tej
 * datoteki. Lint pravilo v `biome.json` to preverja namesto discipline.
 *
 * ---
 *
 * ⛔ **Najpomembnejše dejstvo te datoteke (faza 6).**
 *
 * `createMediaElementSource()` se **ne da razveljaviti**. Element, ki je enkrat
 * pripet grafu, ostane pripet do smrti: njegov zvok od tam naprej teče izključno
 * skozi graf. Če tak element pozneje naloži cross-origin vir **brez** CORS glav,
 * vozlišče odda ničle — zvok utihne, brez napake in brez opozorila (R3 §1).
 *
 * Iz tega sledi celotna zgradba spodaj:
 *   · graf nastane šele, ko ga uporabnik res rabi (`opts.wantsAudioGraph`) IN
 *     je pot CORS-varna (`route.corsSafe`),
 *   · prehod nazaj v direktno pot ne pomeni »razstavi graf«, ampak
 *     **zamenjaj element** — glej `swapElement()`.
 *
 * ---
 *
 * ⚠️ **Slepa ulica, ki je ne ponavljaj.** Da bi pavza na zaklenjenem zaslonu
 * iOS obdržala avdio sejo, je bil element med pavzo puščen teči **utišan**.
 * Preverjeno na napravi 2026-07-19: medijski center je vseeno **takoj**
 * preklopil na Apple Music. WebKit utišanega predvajanja ne šteje za
 * predvajanje — namenoma, da aplikacije seje ne držijo s tišino.
 *
 * Poskus torej ni deloval, med pavzo pa je prenašal tok. Odstranjen.
 * Nadaljevanje z zaklenjenega zaslona je omejitev platforme; glej HANDOFF §0a.
 */

export interface WebAudioPlayerOptions {
  backoff?: BackoffPolicy;
  /** Prag straže živosti; testi ga podajo izrecno. */
  livenessMs?: number;
  /** Testi jih podajo izrecno; sicer se zaznajo iz okolja. */
  capabilities?: AudioCapabilities;
}

/**
 * Koliko časa sme trajati, da se po `play()` položaj premakne.
 *
 * Ni merjena vrednost — na mobilnem omrežju se povezava na živ tok vzpostavlja
 * opazno dlje kot na WiFi. Prenizek prag ponovno zažene tok, ki bi ravno
 * stekel; previsok pusti uporabnika strmeti v mrtev gumb.
 */
const LIVENESS_MS = 12_000;

/**
 * `fftSize` analizatorja (R3 §4 priporoča 256–512).
 *
 * **512, torej 256 predalov.** Prvotnih 256 (128 predalov) je bilo dovolj za
 * telefon, na namizju pa vizualizator razpne čez 1900px in tam je uporabnih
 * predalov manj kot palic — sosede bi dobile isti odčitek in spekter bi bil
 * videti stopničasto. Zgornja meja je zato fizikalna in ne oblikovalska;
 * `visualizer.ts` število palic omeji prav s `frequencyBinCount`.
 */
const ANALYSER_FFT = 512;

/**
 * Glajenje med okvirji. Brez njega palice trzajo tako hitro, da je videti kot
 * šum in ne kot glasba.
 */
const ANALYSER_SMOOTHING = 0.75;

/** Q filtrov ≈ ena oktava — pasovi so oktavno razmaknjeni (`EQ_BANDS`). */
const FILTER_Q = 1.1;

/**
 * Blago pojemanje ob menjavi postaje (faza 7, D1).
 *
 * ⛔ **Zakaj sploh poči.** Pripis novega `src` (ali `pause()` na živem toku)
 * odreže val na **poljubni amplitudi**. Skok z ±0,7 na 0 v enem vzorcu je
 * širokopasoven impulz — v zvočniku se sliši kot udarec. Opaženo na napravi
 * 2026-07-19 in zapisano v D1 kot »kick«.
 *
 * ⛔ **Zakaj ne pojemamo navzdol.** Pojemanje pred rezom bi zahtevalo čakanje,
 * čakanje pa je natanko tisto, česar med gesto in `play()` ne smemo imeti —
 * WebKit predvajanje zavrne (glej `startPlayback` v `domain/playback.ts`).
 * Zato gre element v tišino **v isti nalogi**: tišina pred rezom je brezplačna
 * in rez pri ničli se ne sliši.
 *
 * 🔴 **Tišino nosi `muted`, NE `volume` — in to je bistvo popravka**
 * (izmerjeno na iPhonu 2026-07-21). Prva izvedba je uporabila `audio.volume = 0`
 * in je bila na iOS **mrtva koda**: WebKit `volume` ignorira. Uporabnik je
 * udarec slišal naprej in ga je opisal točno — *»poči zadnja vsebina prejšnje
 * postaje«*, torej rep starega medpomnilnika.
 *
 * `muted` deluje povsod, tudi na iOS. Ker je binaren, na iOS vzpona ni — a
 * tam ga tudi ni mogoče izvesti in nov tok se tako ali tako začne iz tišine.
 *
 * Navzgor imamo čas: ko nov tok res steče (`playing`), tišina odpade, in kjer
 * je glasnost nastavljiva, se v ~220 ms povzpne na uporabnikovo.
 */
const FADE_IN_MS = 220;

/** Korakov vzpona. Osem pri 220 ms je ~27 ms na korak — pod pragom slišnosti. */
const FADE_IN_STEPS = 8;

/** Sekunde: rampa ojačitve, da premik drsnika ne poči v zvočniku. */
const GAIN_RAMP_S = 0.03;

/**
 * Ali je to WebKit na iOS/iPadOS (P2 §6.5).
 *
 * WebKit `createMediaElementSource()` za **žive tokove** ne podpira: graf se
 * postavi, zvoka pa ni. Zaznave ob izvajanju ni — zato je edina poštena pot
 * ugotoviti platformo vnaprej in EQ tam **odpovedati na glas** (toast), ne
 * tiho (P2 §6.5).
 *
 * iPadOS se od 13 naprej predstavlja kot `Macintosh`; loči ga večdotični
 * zaslon, ki ga namizni Mac nima.
 */
function isIosWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

function detectCapabilities(): AudioCapabilities {
  const graph = typeof AudioContext !== 'undefined' && !isIosWebKit();
  return {
    // EQ in analizator stojita ali padeta skupaj: oba potrebujeta isti
    // `MediaElementAudioSourceNode`, in ta na iOS za tokove ne dela.
    eq: graph,
    analyser: graph,
    outputDeviceSelect:
      typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype,
    // Web Audio na ne-CORS viru odda ničle — brez proxyja EQ ne more delovati.
    requiresProxyForEq: true,
    /**
     * ⛔ **Na iOS glasnost ni programsko nastavljiva** (izmerjeno na iPhonu
     * 2026-07-21). WebKit `volume` ignorira in ob branju vrne `1`.
     *
     * To je bilo tiho vse od faze 1: postopno utišanje časovnika spanja na
     * iPhonu **ni delovalo nikoli**, ker gre skozi isto lastnost. Opazilo se
     * je šele, ko je faza 7 dodala viden drsnik in ta ni delal ničesar.
     */
    volume: !isIosWebKit(),
    // Kdo hrani medij pri HLS. Odloča o poti toka, ne o zmožnosti predvajanja.
    hlsMse: hlsUsesMse(),
  };
}

function clampVolume(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.min(1, Math.max(0, v));
}

/** Graf je ali cel ali nobeden — zato en objekt in ne pet spremenljivk. */
interface AudioGraph {
  ctx: AudioContext;
  /** Vhod verige; sem se pripne vir elementa. */
  input: AudioNode;
  filters: BiquadFilterNode[];
  analyser: AnalyserNode;
}

export function createWebAudioPlayer(options: WebAudioPlayerOptions = {}): AudioPort {
  const policy = options.backoff ?? STREAM_BACKOFF;
  const emitter = createAudioEmitter();
  const capabilities = options.capabilities ?? detectCapabilities();

  let current: { station: Station; opts: PlayOptions } | null = null;
  let state: PlaybackState = 'idle';
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Loči odzive tekočega toka od odzivov toka, ki smo ga že opustili. */
  let generation = 0;

  /** Utišanje, ki ga je zahteval uporabnik. */
  let userMuted = false;

  /**
   * Utišanje, ki traja samo čez rez ob menjavi postaje.
   *
   * ⛔ **Ločeno od `userMuted` in to ni podrobnost.** Če bi rez pisal v isto
   * spremenljivko, bi menjava postaje uporabniku vklopila ali izklopila zvok
   * mimo njegove volje — in ob `playing` bi mu tišino, ki jo je zahteval sam,
   * tiho odpravila.
   */
  let transitionMuted = false;

  /**
   * Menjava vira je **v teku** — od pripisa `src` do trenutka, ko nov tok
   * steče (ali odpove).
   *
   * ⛔ **Obstaja zaradi medijske seje, ne zaradi zvoka.** Pripis novega `src`
   * po specifikaciji požene algoritem nalaganja, ta pa na elementu sproži
   * dogodek `pause`. Naš poslušalec ga je doslej razumel kot »sistem nam je
   * vzel zvok« in objavil `idle` — sredi menjave postaje, **po** tem, ko je
   * `play()` že objavil `loading`.
   *
   * Zaporedje stanj, ki ga je videla medijska seja, je bilo torej:
   * `playing → idle → loading → idle → playing`. Vsak `idle` se prevede v
   * `playbackState = 'paused'`, in za sistem to pomeni »ta aplikacija ne
   * predvaja več«.
   *
   * Ujema se z obema prijavama z naprav (2026-07-21):
   *   · iOS — *»za trenutek pokaže Apple Music izbor, potem se vrne Nesywave
   *     in postaja se preklopi«*: seja odpade in jo prevzame naslednji kandidat,
   *   · Android — *»komande se skrijejo, ko preklopim«*: Chromium obvestilo ob
   *     nekrmiljeni seji pod keyguardom prekliče **takoj** (glej HANDOFF).
   *
   * ---
   *
   * 🔴 **Izmerjeno na napravi 2026-07-21: to NI bil vzrok.** Utrip Apple Music
   * v senčniku ostaja tudi po tem popravku. Sejo torej odnese to, da element
   * ob pripisu novega vira dejansko izgubi medij — sistem gleda element, ne
   * `playbackState`. Vprašanje ostaja odprto ([D3 #29](../../../docs/04-delivery/D3-open-questions.md)).
   *
   * ⚠️ **Zastavica kljub temu ostane, in nosi ceno: duši pravi `pause`.**
   * Med menjavo vira `idle` ne gre ven — tudi takrat ne, kadar nam iOS v tem
   * oknu **res** vzame avdio sejo. Vmesnik bi takrat kazal »predvaja« brez
   * zvoka, dokler ga ne ujame straža živosti (12 s).
   *
   * **Če se pojavi prijava »kaže, da igra, zvoka pa ni«, začni tukaj.**
   */
  let swappingSource = false;

  /**
   * Ustavitev je bila namerna — uporabnikova ali naša ob menjavi postaje.
   *
   * `audio.pause()` in pripis novega `src` **zavrneta obljubo `play()` v teku**
   * z `AbortError`. Brez te zastavice je ta zavrnitev padla v `scheduleRetry()`
   * in zvok, ki ga je uporabnik pravkar ustavil, se je čez sekundo sam vrnil.
   */
  let userPaused = false;

  const livenessMs = options.livenessMs ?? LIVENESS_MS;
  let liveness: ReturnType<typeof setTimeout> | null = null;
  /** Položaj ob oborožitvi straže; napredek od tod je edini dokaz živosti. */
  let livenessFrom = -1;

  /* ── Web Audio graf (faza 6) ─────────────────────────────────────── */

  let graph: AudioGraph | null = null;
  let source: MediaElementAudioSourceNode | null = null;
  /** Element, ki je pripet grafu. Pripetost je **nepovratna** — glej glavo. */
  let graphElement: HTMLAudioElement | null = null;
  /** Zadnje zahtevane ojačitve; `null` = EQ ni dejaven. */
  let eqGains: number[] | null = null;

  /**
   * Element se med tekom **zamenja** (glej `swapElement()`), zato je `let` in
   * ne `const`. Vsi poslušalci berejo `audio` ob klicu, ne ob registraciji.
   */
  let audio: HTMLAudioElement;
  let detachListeners: () => void;

  /* ── HLS (faza 6) ────────────────────────────────────────────────── */

  /** Živ priklop `hls.js`; `null` pri native HLS in pri navadnem toku. */
  let hlsSession: HlsSession | null = null;
  /**
   * Modul se prenaša — vira element še **nima**.
   *
   * ⛔ **Zato `play()` v tem oknu ne sme poklicati `audio.play()`.** Element brez
   * vira zavrne z `NotSupportedError`, kar bi bila odpoved zaradi našega
   * vrstnega reda in ne zaradi toka. Namera se zapomni in izvede ob priklopu.
   */
  let hlsLoading = false;
  /** `play()` je bil zahtevan, medtem ko se je modul še prenašal. */
  let hlsWantsPlay = false;

  function detachHls(): void {
    if (hlsSession === null) return;
    hlsSession.destroy();
    hlsSession = null;
  }

  /**
   * Ali tekoča postaja gre skozi `hls.js`.
   *
   * Bere se **ob klicu** in ne ob nalaganju: element se med tekom zamenja
   * (`swapElement()`), postaja pa tudi.
   */
  function usingHlsJs(): boolean {
    return current?.station.isHls === true && capabilities.hlsMse;
  }

  /**
   * Priklopi `hls.js`, sinhrono ali po prenosu modula.
   *
   * Straža generacije je tu nujna: med prenosom modula uporabnik lahko že
   * zamenja postajo, in priklop na star naslov bi mu zavrtel prejšnjo.
   */
  function startHls(url: string): void {
    const ready = loadedHls();
    if (ready !== null) {
      hlsSession = attachHls(ready, audio, url, { onFatal: onHlsFatal });
      // Knjižnica na tem brskalniku ni podprta — navaden `src` je zadnja
      // poštena možnost; native HLS zna več brskalnikov, kot jih prizna.
      if (hlsSession === null) audio.src = url;
      return;
    }

    hlsLoading = true;
    const mine = generation;
    void loadHls()
      .then((mod) => {
        if (mine !== generation || current === null) return;
        hlsLoading = false;
        hlsSession = attachHls(mod, audio, url, { onFatal: onHlsFatal });
        if (hlsSession === null) audio.src = url;
        if (!hlsWantsPlay) return;
        hlsWantsPlay = false;
        armLiveness();
        void audio.play().catch(handlePlayRejection);
      })
      .catch(() => {
        if (mine !== generation) return;
        hlsLoading = false;
        hlsWantsPlay = false;
        // Prenos knjižnice je padel. Ponavljanje z zamikom je smiselno —
        // omrežje je isto, ki bo čez hip nosilo tok.
        scheduleRetry();
      });
  }

  function onHlsFatal(): void {
    // Ponovni poskus je že oborožen: knjižnica ob istem izpadu odda več napak.
    if (retryTimer !== null) return;
    scheduleRetry();
  }

  function createElement(): HTMLAudioElement {
    const element = new Audio();
    element.preload = 'none';
    element.autoplay = false;
    // Piškotki ne gredo ne navzgor ne navzdol; privzeto ostane brez CORS,
    // ker ga direktni tok ne prenese (T1 §4).
    element.crossOrigin = null;
    return element;
  }

  function applyMuted(): void {
    audio.muted = userMuted || transitionMuted;
  }

  /** Konec prehoda: uporabnikova tišina ostane, naša odpade. */
  function endTransitionMute(): void {
    if (!transitionMuted) return;
    transitionMuted = false;
    applyMuted();
  }

  /**
   * Menjava vira je končana — po uspehu ali po odpovedi.
   *
   * ⚠️ **Klicati jo je treba na VSAKI poti ven**, ne le ob uspehu. Zastavica,
   * ki bi obvisela, bi požrla tudi pravi `pause` — tistega, s katerim iOS
   * pove, da nam je vzel avdio sejo — in vmesnik bi kazal »predvaja«, zvoka
   * pa ne bi bilo. Straža živosti (12 s) je zadnja varovalka, ne prva.
   */
  function endSourceSwap(): void {
    swappingSource = false;
  }

  /* ── Blago pojemanje ob menjavi postaje (faza 7) ─────────────────── */

  let fadeTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Ciljna glasnost je **zapis v `current.opts`**, ne posneta vrednost.
   *
   * Tako jo sme `setVolume()` med vzponom spremeniti in vzpon konča na novi —
   * kar časovnik spanja med pojemanjem res počne, vsako sekundo.
   */
  function volumeTarget(): number {
    return clampVolume(current?.opts.volume ?? 1);
  }

  function cancelFade(): void {
    if (fadeTimer === null) return;
    clearInterval(fadeTimer);
    fadeTimer = null;
  }

  function startFadeIn(): void {
    cancelFade();
    // Kjer glasnost ni nastavljiva (iOS), je odprava tišine ves prehod.
    if (!capabilities.volume) return;
    if (audio.volume >= volumeTarget()) {
      // Nadaljevanje po pavzi in prvo predvajanje: glasnost ni bila znižana,
      // torej ni česa dvigovati.
      audio.volume = volumeTarget();
      return;
    }

    let step = 0;
    fadeTimer = setInterval(
      () => {
        step += 1;
        const ratio = Math.min(1, step / FADE_IN_STEPS);
        // `audio` se bere ob vsakem tiku: med vzponom se element lahko
        // zamenja (`swapElement()`) in pisanje v odvrženega ne bi šlo nikamor.
        audio.volume = clampVolume(volumeTarget() * ratio);
        if (ratio >= 1) cancelFade();
      },
      Math.round(FADE_IN_MS / FADE_IN_STEPS),
    );
  }

  function clearLiveness(): void {
    if (liveness === null) return;
    clearTimeout(liveness);
    liveness = null;
  }

  /**
   * Straža proti tihi odpovedi.
   *
   * `audio.play()` na elementu, ki mu je izvor med pavzo spustil povezavo,
   * obljube **ne razreši in ne zavrne** — čaka na `HAVE_FUTURE_DATA`, ki na
   * mrtvem toku ne pride nikoli. Brez straže stanje obstane na `loading`, ki ga
   * vmesnik šteje za dejavno, in gumb postane zanka dveh dotikov.
   *
   * Straža se zato oboroži **pred** klicem: preverjanje za `await` do izraza
   * nikoli ne pride.
   *
   * ⚠️ Dogodek `playing` ni dokaz. WebKit ga sproži tudi na zastarelem
   * medpomnilniku, ki se ne premika — zvoka pa ni. Edini pošten dokaz je
   * napredek `currentTime`, zato stražo razoroži `timeupdate`, ne `playing`.
   */
  function armLiveness(): void {
    clearLiveness();
    livenessFrom = audio.currentTime;
    liveness = setTimeout(() => {
      liveness = null;
      if (current === null || userPaused) return;
      if (audio.currentTime > livenessFrom) return;
      scheduleRetry();
    }, livenessMs);
  }

  function emitState(next: PlaybackState): void {
    // `reconnecting` se ponovi ob vsakem poskusu — številka poskusa je vsebina.
    if (state === next && next !== 'reconnecting') return;
    state = next;
    emitter.emit('statechange', { state, attempt });
  }

  function cancelRetry(): void {
    if (retryTimer === null) return;
    clearTimeout(retryTimer);
    retryTimer = null;
  }

  function releaseElement(): void {
    // Najprej priklop, šele nato element: `hls.js`, ki bi ga preživel, bi
    // segmente prenašal naprej — pri živem toku brez konca.
    detachHls();
    hlsLoading = false;
    hlsWantsPlay = false;
    audio.pause();
    // `src = ''` bi se razrešil v naslov strani in sprožil napako; atribut
    // je treba odstraniti in šele nato pustiti elementu, da spusti povezavo.
    audio.removeAttribute('src');
    audio.load();
  }

  /**
   * Graf nastane **enkrat na sejo** in šele ob prvi dejanski potrebi.
   *
   * `AudioContext` se ne ustvari ob zagonu aplikacije: brskalniki jih na
   * zavihek omejujejo, ustvarjen kontekst pa drži avdio strojno opremo budno.
   * Kdor EQ in vizualizatorja ne vklopi, ga zato nikoli ne dobi.
   *
   * Kontekst se **ne uspava**, ko vir odpade. Uspavanje bi prihranilo malo,
   * neuspešen `resume()` pa bi pomenil tišino — natanko razred napake, ki se
   * mu ta faza izogiba.
   */
  function buildGraph(): AudioGraph | null {
    if (graph !== null) return graph;
    if (!capabilities.eq && !capabilities.analyser) return null;

    let ctx: AudioContext;
    try {
      ctx = new AudioContext();
    } catch {
      return null;
    }

    const filters: BiquadFilterNode[] = [];
    let head: AudioNode | null = null;
    let tail: AudioNode | null = null;

    for (const [index, hz] of EQ_BANDS.entries()) {
      const filter = ctx.createBiquadFilter();
      /**
       * Robna pasova sta **polici**, vmesni so zvonovi.
       *
       * Zvon pri 31 Hz bi pustil vse pod seboj nedotaknjeno in drsnik »31« bi
       * na večini glasbe naredil skoraj nič. Polica prime cel rep spektra —
       * kar uporabnik od skrajnega drsnika tudi pričakuje.
       */
      filter.type =
        index === 0 ? 'lowshelf' : index === EQ_BANDS.length - 1 ? 'highshelf' : 'peaking';
      filter.frequency.value = hz;
      filter.Q.value = FILTER_Q;
      filter.gain.value = 0;

      if (tail === null) head = filter;
      else tail.connect(filter);
      tail = filter;
      filters.push(filter);
    }

    const analyser = ctx.createAnalyser();
    analyser.fftSize = ANALYSER_FFT;
    analyser.smoothingTimeConstant = ANALYSER_SMOOTHING;

    if (head === null || tail === null) return null;
    tail.connect(analyser);
    analyser.connect(ctx.destination);

    graph = { ctx, input: head, filters, analyser };
    applyGains();
    return graph;
  }

  function applyGains(): void {
    const active = graph;
    if (active === null) return;
    const gains = eqGains;
    const at = active.ctx.currentTime;
    active.filters.forEach((filter, index) => {
      // `null` in same ničle sta za graf ista stvar: filter pri 0 dB ne
      // spreminja ničesar. Razliko pozna klicno mesto, ne vozlišče.
      const value = gains === null ? 0 : (gains[index] ?? 0);
      filter.gain.setTargetAtTime(value, at, GAIN_RAMP_S);
    });
  }

  /**
   * Pripne element grafu. **Nepovratno za ta element** (glej glavo datoteke).
   *
   * Kliče se šele, ko je znano oboje: uporabnik graf rabi in pot je CORS-varna.
   */
  function attachGraph(element: HTMLAudioElement): void {
    const active = buildGraph();
    if (active === null) return;
    if (graphElement === element) return;

    source?.disconnect();
    try {
      source = active.ctx.createMediaElementSource(element);
    } catch {
      // Element je že pripet drugemu kontekstu. Zvok mora teči naprej brez
      // grafa — tiho brez zvoka bi bilo veliko slabše od EQ, ki ne prime.
      source = null;
      return;
    }
    source.connect(active.input);
    graphElement = element;
    // Kontekst se ob ustvarjanju pogosto rodi `suspended`; klic stoji v isti
    // nalogi kot uporabnikova gesta, obljube pa namenoma NE čakamo.
    void active.ctx.resume().catch(() => {});
  }

  /**
   * Sveži element namesto tistega, ki je pripet grafu.
   *
   * Edina pot nazaj v direktno predvajanje. `createMediaElementSource()` je
   * nepovraten, zato bi star element na viru brez CORS oddal tišino.
   */
  function swapElement(): void {
    const old = audio;
    detachListeners();
    // Priklop živi na elementu, ne na postaji: odvržen element bi ga vlekel s
    // seboj in knjižnica bi naprej prenašala segmente v nič.
    detachHls();
    // Tudi razstavljanje reže val: pavza živega toka pri polni glasnosti poči
    // enako kot menjava vira. `muted` deluje tudi na iOS, `volume` tam ne.
    old.muted = true;
    old.volume = 0;
    old.pause();
    old.removeAttribute('src');
    old.load();

    // Mrtev vir ne sme ostati priklopljen: sicer v verigo naprej teče vozlišče,
    // ki ga nihče ne hrani.
    source?.disconnect();
    source = null;
    graphElement = null;

    audio = createElement();
    detachListeners = attachListeners(audio);
  }

  function applySource(): void {
    if (current === null) return;
    const { station, opts } = current;

    /**
     * Graf sme nastati samo ob obojem hkrati (T1 §4):
     *   a) uporabnik ga res rabi — EQ ni raven ali je vizualizator vklopljen,
     *   b) pot je CORS-varna, torej gre tok prek Workerja.
     * Tretji pogoj je platforma: na iOS `createMediaElementSource()` za tokove
     * ne dela in poštena vrednost je `false` (P2 §6.5).
     */
    const wantsGraph =
      opts.wantsAudioGraph && opts.route.corsSafe && (capabilities.eq || capabilities.analyser);

    // Element, ki je bil kdaj v grafu, se v direktno pot ne sme vrniti.
    if (!wantsGraph && graphElement === audio) swapElement();

    /**
     * Ali bomo z novim `src` odrezali **živ, slišen** tok.
     *
     * Prebrati je treba PRED pripisom in po morebitni menjavi elementa: svež
     * element iz `swapElement()` je ustavljen in brez vira, torej ne reže nič.
     */
    const cutsLiveAudio = !audio.paused && audio.currentSrc !== '';
    cancelFade();

    // crossOrigin mora biti nastavljen PRED src, sicer nima učinka.
    audio.crossOrigin = wantsGraph ? 'anonymous' : null;
    // AirPlay ostane na voljo; pri HLS ga izklopimo, ker ManagedMediaSource
    // na iOS brez tega tiho obvisi in se `sourceopen` sploh ne sproži (T1 §3).
    audio.disableRemotePlayback = station.isHls;

    /**
     * Tišina pred rezom, odprava po `playing` (glej `FADE_IN_MS`).
     *
     * 🔴 **`muted` je nosilec, `volume` je dodatek.** Na iOS `volume` ne stori
     * ničesar; brez `muted` bi rep prejšnje postaje počil naprej — natanko to,
     * kar je bilo prijavljeno z naprave.
     *
     * Vse sinhrono: med gesto in `play()` ne sme biti čakanja.
     */
    transitionMuted = cutsLiveAudio;
    audio.volume = cutsLiveAudio ? 0 : clampVolume(opts.volume);
    userMuted = opts.muted;
    applyMuted();

    // Od tu do `playing` je vsak `pause` na elementu NAŠ, ne sistemov.
    swappingSource = true;

    // Star priklop mora pasti pred novim virom: `hls.js`, ki bi ostal na
    // elementu, bi vanj naprej pisal segmente prejšnje postaje.
    detachHls();
    hlsWantsPlay = false;
    hlsLoading = false;

    if (station.isHls && capabilities.hlsMse) {
      /**
       * Pri MSE vira ne pripišemo mi — pripiše ga knjižnica (`blob:`).
       *
       * Atribut je vseeno treba odstraniti: če je na elementu ostal naslov
       * prejšnje postaje, bi ga `attachMedia()` prekril, star tok pa bi do
       * tedaj že tekel.
       */
      audio.removeAttribute('src');
      startHls(opts.route.url);
    } else {
      // Element NAMENOMA ne razstavimo pred tem. Pripis `src` staro nalaganje
      // prekine sam, avdio seja pa ostane naša — kar je pogoj, da menjava
      // postaje deluje tudi z zaklenjenega zaslona.
      audio.src = opts.route.url;
    }

    if (wantsGraph) attachGraph(audio);
  }

  function fail(kind: AudioErrorKind): void {
    cancelRetry();
    clearLiveness();
    // Varovalo: brez tega bi element po dokončni odpovedi ostal utišan od
    // reza, ki se ni nikoli iztekel, in naslednje predvajanje bi bilo tiho.
    endTransitionMute();
    endSourceSwap();
    emitter.emit('error', { kind, retryable: false });
    emitState('error');
  }

  function scheduleRetry(): void {
    // Uporabnik je ustavil. Ponovni poskus bi mu zvok vrnil brez vprašanja.
    if (current === null || userPaused) return;

    const next = attempt + 1;
    if (!shouldRetry(next, policy)) {
      fail('exhausted');
      return;
    }

    attempt = next;
    // Menjava se je iztekla v ponovni poskus: od tu naprej so `pause` spet
    // sistemovi, ne naši.
    endSourceSwap();
    emitter.emit('error', { kind: 'network', retryable: true });
    emitState('reconnecting');

    const mine = generation;
    cancelRetry();
    retryTimer = setTimeout(
      () => {
        retryTimer = null;
        // `userPaused` se preveri ZNOVA: uporabnik je lahko ustavil med čakanjem.
        if (mine !== generation || current === null || userPaused) return;
        applySource();
        // ⛔ Pri MSE `load()` NE kliči: element bi izpraznil `blob:`, ki ga je
        // pravkar pripela knjižnica, in tok bi obstal na prvem segmentu.
        if (!usingHlsJs()) audio.load();
        if (hlsLoading) {
          hlsWantsPlay = true;
          return;
        }
        armLiveness();
        void audio.play().catch(handlePlayRejection);
      },
      backoffDelay(attempt, policy),
    );
  }

  function handlePlayRejection(error: unknown): void {
    const name = error instanceof Error ? error.name : '';

    /**
     * Prekinitev ni izpad.
     *
     * `AbortError` pomeni, da je obljubo prekinilo dejanje z namenom: pavza
     * (`audio.pause()`) ali menjava postaje (pripis `audio.src`, ki po
     * specifikaciji brezpogojno požene algoritem nalaganja). `userPaused`
     * pokrije še zavrnitve, ki pridejo z drugim imenom.
     *
     * ⚠️ Straže tu NE razorožimo. Ob menjavi postaje pride zavrnitev stare
     * obljube šele po tem, ko je nov `play()` stražo že oborožil — razorožitev
     * bi ubila stražo, ki pripada tekočemu poskusu, ne prejšnjemu.
     */
    if (name === 'AbortError' || userPaused) return;

    if (name === 'NotAllowedError') {
      // Brskalnik zahteva gesto. Ponavljanje ne pomaga — pomaga dotik.
      cancelRetry();
      emitter.emit('error', { kind: 'blocked', retryable: false });
      emitState('error');
      return;
    }

    if (name === 'NotSupportedError') {
      fail('unsupported');
      return;
    }

    scheduleRetry();
  }

  /**
   * Poslušalci se ob menjavi elementa preselijo na novega.
   *
   * `AbortController` je edini način, kako jih odstraniti vse hkrati brez
   * hranjenja petih referenc — pozabljen poslušalec na odvrženem elementu bi
   * ob njegovih dogodkih spreminjal stanje tekočega toka.
   */
  function attachListeners(element: HTMLAudioElement): () => void {
    const controller = new AbortController();
    const opts = { signal: controller.signal };

    element.addEventListener(
      'playing',
      () => {
        if (current === null) return;
        attempt = 0;
        cancelRetry();
        // Zvok res teče: naša tišina odpade, uporabnikova ostane.
        endTransitionMute();
        endSourceSwap();
        startFadeIn();
        emitState('playing');
      },
      opts,
    );

    element.addEventListener(
      'waiting',
      () => {
        // Kratki zastoji sredi predvajanja so normalni; `loading` javimo samo,
        // dokler tok še ni stekel, sicer vrstica utripa ob vsakem medpomnjenju.
        if (state === 'playing' || state === 'reconnecting') return;
        emitState('loading');
      },
      opts,
    );

    // Napredek položaja je edini dokaz, da tok res teče. Zato razoroži stražo
    // ta dogodek in ne `playing` — glej `armLiveness()`.
    element.addEventListener(
      'timeupdate',
      () => {
        clearLiveness();
      },
      opts,
    );

    element.addEventListener(
      'pause',
      () => {
        // Element ne ustavimo vedno mi: iOS to naredi ob prevzemu avdio seje,
        // sistem pa tudi ob klicu ali drugi prekinitvi. Brez tega bi vmesnik
        // kazal »predvaja«, zvoka pa ne bi bilo — natanko razkorak, ki smo ga
        // opazili na napravi.
        if (current === null) return;

        /**
         * ⛔ **Med menjavo vira je ta `pause` NAŠ in ne pomeni ustavitve.**
         *
         * Pripis `src` po specifikaciji požene algoritem nalaganja, ta pa
         * element ustavi in sproži `pause`. Objaviti ob tem `idle` pomeni
         * medijski seji povedati »ne predvajamo več« — sredi menjave postaje,
         * ki jo je uporabnik pravkar zahteval. Sistem sejo takrat preda
         * naslednjemu kandidatu (glej `swappingSource`).
         */
        if (swappingSource) return;

        clearLiveness();
        emitState('idle');
      },
      opts,
    );

    element.addEventListener(
      'error',
      () => {
        if (current === null || element.currentSrc === '') return;
        scheduleRetry();
      },
      opts,
    );

    element.addEventListener(
      'ended',
      () => {
        // Živ tok se ne konča sam. Če se, je izvor spustil povezavo.
        if (current === null) return;
        scheduleRetry();
      },
      opts,
    );

    return () => {
      controller.abort();
    };
  }

  audio = createElement();
  detachListeners = attachListeners(audio);

  return {
    capabilities,

    get loadedStationId() {
      return current?.station.id ?? null;
    },

    get canResume() {
      /**
       * Nadaljevanje z `resume()` je varno samo, dokler element **še ima
       * naložen medij**. iOS ga ob pavzi v ozadju sprosti — takrat je edina
       * pot do zvoka polni `play()` (reload), ki dela tudi z zaklenjenega
       * zaslona (⏮ ⏭ ga uporabita in delujeta; gol `play` ne).
       *
       * ⚠️ **Namenoma brez `networkState === NETWORK_EMPTY`.** Na iOS je ta
       * `EMPTY` tudi po navadni pavzi v ospredju in bi ob vsakem nadaljevanju
       * sprožil ponovno medpomnjenje — slišen »kick« in dvojni dotik (to je bila
       * napaka umaknjenega popravka D). `HAVE_NOTHING` pomeni, da element res
       * nima podatka; ohranjen medpomnilnik ga ne sproži.
       */
      return current !== null && audio.error === null && audio.readyState !== audio.HAVE_NOTHING;
    },

    async load(station, opts) {
      cancelRetry();
      clearLiveness();
      generation += 1;
      attempt = 0;
      // Nova postaja je nov namen. Zavrnitev obljube prejšnje je `AbortError`
      // in jo prestreže `handlePlayRejection`.
      userPaused = false;

      current = { station, opts };
      applySource();

      /**
       * ⛔ **`loading`, ne `idle` — in to ni kozmetika.**
       *
       * `idle` se v medijski seji prevede v `playbackState = 'paused'`
       * (`media-session.ts`). Ob menjavi postaje je bil to **prvi** od dveh
       * trenutkov, ko je sistem izvedel, da ne predvajamo več — drugi je bil
       * dogodek `pause` iz algoritma nalaganja (glej `swappingSource`).
       *
       * `loading` je poleg tega **resnica**: vir je pravkar pripisan in
       * `play()` sledi v isti nalogi. Vmesnik takrat pokaže »Povezovanje…«,
       * kar je natanko to, kar se dogaja.
       */
      emitState('loading');

      // Bitrate in kodek sta ob nalaganju znana iz kataloga; iz ICY glav ju
      // dopolni kaskada metapodatkov (faza 3).
      emitter.emit('streaminfo', {
        ...(station.bitrate === undefined ? {} : { bitrate: station.bitrate }),
        ...(station.codec === undefined ? {} : { codec: station.codec }),
      });
    },

    async play() {
      if (current === null) throw new Error('AudioPort: play() pred load()');

      cancelRetry();
      userPaused = false;
      emitState('loading');

      /**
       * ⛔ **Modul `hls.js` se še prenaša — element vira nima.**
       *
       * `audio.play()` bi tu zavrnil z `NotSupportedError` in odpoved bi
       * pripisali toku, ki še ni imel priložnosti. Namera se zato zapomni;
       * izvede jo priklop (`startHls`), takoj ko je knjižnica na voljo.
       *
       * Tega okna na WebKitu ni: tam gre HLS po native poti in `src` je
       * pripisan sinhrono, torej ostane predvajanje v isti nalogi kot gesta.
       */
      if (hlsLoading) {
        hlsWantsPlay = true;
        return;
      }

      armLiveness();
      try {
        await audio.play();
      } catch (error) {
        handlePlayRejection(error);
      }
    },

    pause() {
      cancelRetry();
      clearLiveness();
      attempt = 0;
      userPaused = true;
      // Pavza sredi prehoda bi element pustila tih in nadaljevanje bi bilo
      // videti kot mrtev gumb. Tišina in glasnost gresta nazaj takoj.
      cancelFade();
      endTransitionMute();
      // Uporabnik je ustavil: njegov `pause` mora priti skozi.
      endSourceSwap();
      // Namera predvajanja, ki čaka na `hls.js`, s tem odpade — sicer bi mu
      // zvok stekel, ko se modul prenese, sekundo za tem, ko ga je ustavil.
      hlsWantsPlay = false;
      audio.volume = volumeTarget();
      // Že oborožen časovnik ponovnega poskusa odpade na straži generacije.
      generation += 1;
      // Vir ostane: menjava postaje in nadaljevanje v ospredju s tem stečeta
      // brez ponovnega nalaganja. Na zaklenjenem zaslonu nadaljevanja ni —
      // to je omejitev iOS, ne naša izbira. Glej HANDOFF §0a.
      audio.pause();
      emitState('idle');
    },

    resume() {
      if (current === null) throw new Error('AudioPort: resume() pred load()');

      cancelRetry();
      userPaused = false;

      // Element je lahko ustavil tudi sistem, ne le mi — iOS to naredi, ko
      // aplikaciji vzame avdio sejo. Stanja zato ne domnevamo, ampak ga
      // preberemo; sicer bi vmesnik javil »predvaja«, zvoka pa ne bi bilo.
      if (!audio.paused) {
        emitState('playing');
        return Promise.resolve();
      }

      // Nadaljevanje med prenosom knjižnice: element vira nima, zato velja
      // isto kot pri `play()` — namero izvede priklop.
      if (hlsLoading) {
        hlsWantsPlay = true;
        emitState('loading');
        return Promise.resolve();
      }

      /**
       * ⛔ **Tu NE nalagaj vira znova (poskušano in umaknjeno 2026-07-20).**
       *
       * Popravek D je tu ob `audio.error || networkState === NETWORK_EMPTY`
       * klical `applySource() + audio.load()`, da bi mrtev element po dolgi
       * pavzi oživel. Na iOS je `networkState` po **vsaki** pavzi `NETWORK_EMPTY`
       * — sistem ob pavzi vir sprosti — zato se je ponovno nalaganje sprožilo ob
       * vsakem nadaljevanju: slišen ponovni »kick« medpomnjenja, `load()` pa je
       * prekinil `play()` v isti nalogi, tako da je bil potreben **drugi dotik**.
       * Izmerjeno na napravi.
       *
       * Straža živosti (`armLiveness`, 12 s) mrtev element tako ali tako ujame
       * in ponovno poveže — to je varovalka za dolgo pavzo. Tveganje visečega
       * `play()` ostaja odprto in ga je treba **izmeriti na napravi**, preden se
       * ga znova poskuša popraviti (HANDOFF).
       */

      emitState('loading');
      armLiveness();
      // Sinhron klic: med gesto in play() ne sme biti nobenega `await`,
      // sicer WebKit predvajanje zavrne.
      return audio.play().catch(handlePlayRejection);
    },

    stop() {
      cancelRetry();
      clearLiveness();
      cancelFade();
      endTransitionMute();
      endSourceSwap();
      generation += 1;
      attempt = 0;
      userPaused = true;
      releaseElement();
      current = null;
      emitState('idle');
    },

    setVolume(v) {
      const value = clampVolume(v);
      if (current !== null) current = { ...current, opts: { ...current.opts, volume: value } };
      // ⚠️ **Med vzponom se glasnost NE postavi neposredno.** Zapis v `opts` je
      // dovolj: vzpon bere cilj ob vsakem tiku in konča na novi vrednosti.
      // Neposreden pripis bi vzpon prekinil s skokom — natanko tisto, čemur
      // se izogiba (in časovnik spanja piše vsako sekundo).
      if (fadeTimer === null) audio.volume = value;
    },

    setMuted(m) {
      userMuted = m;
      applyMuted();
      if (current !== null) current = { ...current, opts: { ...current.opts, muted: m } };
    },

    /**
     * Ojačitve se sprejmejo **vedno**, tudi kadar grafa še ni.
     *
     * Vrstni red je namreč obraten od pričakovanega: uporabnik premakne drsnike
     * in šele naslednje nalaganje toka postavi graf. Zavrnitev bi pomenila, da
     * EQ »ne prime«, dokler ga ne nastaviš dvakrat.
     */
    setEq(gains) {
      eqGains = gains === null ? null : normaliseGains(gains);
      applyGains();
    },

    getAnalyser(): AnalyserLike | null {
      // Brez pripetega vira analizator meri tišino. `null` je poštenejši od
      // ravne črte, ki je videti kot delujoč, a tih zvok.
      return graph !== null && graphElement !== null ? graph.analyser : null;
    },

    on(event, fn) {
      return emitter.on(event, fn);
    },
  };
}
